import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useTableStore, {
  ALL_TABLE_COLUMNS,
  ALWAYS_VISIBLE_COLUMNS,
} from "../../store/useTabStore";
import LoadingSpinner from "../ui/LoadingSpinner";
import SearchBar from "./components/SearchBar";
import TableHeader from "./components/TableHeader";
import TablePagination from "./components/TablePagination";
import TableRow from "./components/TableRow";
import useTableData from "./hooks/useTableData";
import { SearchType, SortConfig } from "./types";

const ColumnSelector: React.FC = () => {
  const { visibleColumns, toggleColumnVisibility } = useTableStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className="relative z-10 inline-block mb-2 text-left"
      ref={dropdownRef}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Columns
        <svg
          className="w-5 h-5 ml-2 -mr-1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 w-56 mt-2 overflow-y-auto bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-96">
          <div className="py-1">
            {ALL_TABLE_COLUMNS.map((col) => (
              <label
                key={col.id}
                className="flex items-center px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 mr-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={visibleColumns.includes(col.id)}
                  onChange={() => toggleColumnVisibility(col.id)}
                  disabled={ALWAYS_VISIBLE_COLUMNS.includes(col.id)}
                />
                {col.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Table: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchType = location.pathname.substring(
    location.pathname.lastIndexOf("/") + 1
  );

  const { fetchColumns, activeFilters: ZustandActiveFilters, structureSearchParams } = useTableStore();

  const currentPage = searchParams.get("page")
    ? parseInt(searchParams.get("page")!)
    : 1;

  const [pageSize, setPageSize] = useState(() =>
    searchParams.get("page_size")
      ? parseInt(searchParams.get("page_size")!)
      : 10
  );
  const pageSizeOptions = [10, 20, 50, 100];

  const [sortConfig, setSortConfig] = useState<SortConfig>(() => ({
    column: searchParams.get("sort_column") || "",
    direction: searchParams.get("sort_direction") === "DESC" ? "DESC" : "ASC",
  }));

  const activeSearchText = searchParams.get("search") || "";

  const [initialParamsApplied, setInitialParamsApplied] = useState(false);

  useEffect(() => {
    setInitialParamsApplied(true);
  }, [ZustandActiveFilters]);

  const { data, loading, totalPages } = useTableData({
    searchType: searchType as SearchType,
    searchText: activeSearchText,
    currentPage,
    pageSize,
    sortConfig,
    isReadyToFetch: initialParamsApplied,
  });

  const updateURLParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams.toString());

    if (pageSize !== 10) newParams.set("page_size", pageSize.toString());
    else newParams.delete("page_size");

    if (activeSearchText) newParams.set("search", activeSearchText);
    else newParams.delete("search");

    if (sortConfig.column) {
      newParams.set("sort_column", sortConfig.column);
      if (sortConfig.direction === "DESC")
        newParams.set("sort_direction", "DESC");
      else newParams.delete("sort_direction");
    } else {
      newParams.delete("sort_column");
      newParams.delete("sort_direction");
    }

    if (searchParams.toString() !== newParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [pageSize, activeSearchText, sortConfig, searchParams, setSearchParams]);

  useEffect(() => {
    if (initialParamsApplied) {
      updateURLParams();
    }
    // updateURLParams itself guards against writing an identical URL, so listing it
    // here doesn't cause a loop even though its identity tracks searchParams.
  }, [initialParamsApplied, updateURLParams]);

  useEffect(() => {
    fetchColumns();
  }, [fetchColumns]);

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      const newParams = new URLSearchParams(searchParams.toString());
      if (newPage > 1) {
        newParams.set("page", newPage.toString());
      } else {
        newParams.delete("page");
      }
      setSearchParams(newParams, { replace: true });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete("page");
    setSearchParams(newParams, { replace: true });
  };

  const handleSort = (columnName: string) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig.column === columnName && prevConfig.direction === "ASC"
          ? "DESC"
          : "ASC";
      return { column: columnName, direction: newDirection };
    });
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete("page");
    setSearchParams(newParams, { replace: true });
  };

  const handleSearch = (searchTerm: string) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.delete("page");

    if (searchTerm) {
      newParams.set("search", searchTerm);
    } else {
      newParams.delete("search");
    }
    setSearchParams(newParams, { replace: true });
  };

  const showLoadingSpinner = loading && (!data || !initialParamsApplied);
  const showNoDataMessage =
    initialParamsApplied &&
    !loading &&
    (!data?.results || data.results.length === 0);

  return (
    <div className="relative px-8 mt-4 overflow-hidden">
      <div className="flex items-center p-4 bg-white border-b">
        {searchType === "text" ? (
          <div className="flex-1 mr-4">
            <SearchBar onSearch={handleSearch} />
          </div>
        ) : (
          <div className="flex-1"></div>
        )}
        <div className="flex-shrink-0">
          <ColumnSelector />
        </div>
      </div>

      {showLoadingSpinner && <LoadingSpinner />}

      {showNoDataMessage && (
        <div className="flex items-center justify-center p-8 text-gray-500 min-h-[200px]">
          <div className="text-center">
            <svg>
              <path />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchType === "structure" && !structureSearchParams
                ? "Ready for Structure Search"
                : "No data available"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchType === "structure" && !structureSearchParams
                ? "Please draw a molecule and perform a search to see results."
                : "Try adjusting your search criteria or Reset filters to view all data"}
            </p>
          </div>
        </div>
      )}

      {initialParamsApplied && data?.results && data.results.length > 0 && (
        <>
          {data.count > 0 && (
            <TablePagination
              paginationConfig={{
                currentPage,
                pageSize,
                totalItems: data.count,
                pageSizeOptions,
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <TableHeader onSort={handleSort} sortConfig={sortConfig} />
              <tbody className="divide-y divide-gray-200">
                {data.results.map((result, index) => (
                  <TableRow
                    key={result.index ?? index}
                    result={result}
                    index={index}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {data.count > 0 && (
            <TablePagination
              paginationConfig={{
                currentPage,
                pageSize,
                totalItems: data.count,
                pageSizeOptions,
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </>
      )}

      {initialParamsApplied && loading && data && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Table;
